import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Container, Modal } from 'react-bootstrap';
import './Home.css';
import lionsImage from './assets/Logo-myn.png';

function Reservation() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tickets: ''
  });

  const [status, setStatus] = useState(null); // 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [showPopup, setShowPopup] = useState(false); // show/hide modal
  const [ticketAvailability, setTicketAvailability] = useState(null);
  const navigate = useNavigate();
  const selectedTicketCount = Number(formData.tickets);
  const ticketTotal = Number.isInteger(selectedTicketCount) && selectedTicketCount >= 1 && selectedTicketCount <= 10
    ? selectedTicketCount * 25
    : null;

  const updateTicketAvailability = (data) => {
    const reserved = Number(data.reserved);
    const onsell = Number(data.onsell);

    if (Number.isFinite(reserved) && Number.isFinite(onsell)) {
      setTicketAvailability({
        remaining: Math.max(onsell - reserved, 0),
        total: onsell
      });
    }
  };

  useEffect(() => {
    const loadTicketAvailability = async () => {
      try {
        const response = await fetch('https://joulukonsertti.lcmynamaki.fi/haevaraukset');
        if (response.ok) {
          updateTicketAvailability(await response.json());
        }
      } catch (error) {
        console.error('Error loading ticket availability:', error);
      }
    };

    loadTicketAvailability();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    if (status === 'success') navigate('/'); // optional redirect after success
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    const ticketCount = Number(formData.tickets);

    // Validate that input is between 1 and 10
    if (ticketCount < 1 || ticketCount > 10) {
      setStatus('error');
      setErrorMessage('Lippujen määrä voi olla vain 1–10.');
      setShowPopup(true);
      return;
    }

    try {
      // 1️⃣ Fetch current reservation status
      const checkResponse = await fetch('https://joulukonsertti.lcmynamaki.fi/haevaraukset');
      if (!checkResponse.ok) throw new Error('Virhe haettaessa varauksia.');
      const data = await checkResponse.json();

      const reserved = Number(data.reserved);
      const onsell = Number(data.onsell);
      updateTicketAvailability(data);

      // 2️⃣ Check if there’s enough tickets left
      if (reserved + ticketCount > onsell) {
        setStatus('error');
        const remaining = Math.max(onsell - reserved, 0);
        setErrorMessage(
          remaining > 0
            ? `Vain ${remaining} lippua jäljellä. Pienennä lippujen määrää.`
            : 'Kaikki liput on jo varattu.'
        );
        setShowPopup(true);
        return;
      }

      // 3️⃣ Proceed to make the reservation
      const response = await fetch('https://joulukonsertti.lcmynamaki.fi/varaaliput', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status) {
          setStatus('success');
          setTicketAvailability((currentAvailability) => currentAvailability && {
            ...currentAvailability,
            remaining: Math.max(currentAvailability.remaining - ticketCount, 0)
          });
          setFormData({ name: '', email: '', tickets: '' });
          setShowPopup(true);
        } else {
          throw new Error(
            `Sinulle on jo varattu ${data.reserved} lippua. Jos haluat muuttaa varausta, poista ensin vanha varaus ja tee sitten uusi varaus.`
          );
        }
      } else {
        throw new Error('Palvelin palautti virheen.');
      }
    } catch (error) {
      console.error('Error sending reservation:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Varaus epäonnistui. Yritä uudelleen.');
      setShowPopup(true);
    }
  };

  return (
    <div className="home-container">
      <button className="takaisin text-red-500 btn btn-dark" onClick={() => navigate('/')}>
        X
      </button>

      <div className="text-container">
        <p className="title1st-text">Mynämäen joulukonsertti</p>
        <p className="artist">Anne Mattilan joulu</p>
     </div>

      <div className="main-container">
        <p className="price-info mb-1">25 €/kpl</p>
        {ticketAvailability && (
          <p className="text1st mb-1">
            Online lippuja {ticketAvailability.remaining} / {ticketAvailability.total}
          </p>
        )}
        <p className="text1st mb-1">Liput lunastetaan konserttiin saavuttaessa. Ovet avataan tunti ennen konsertin alkua.</p>
        <p className="text1st mb-1">Maksu käteisellä, kortilla tai MobilePayllä.</p>

      <Container className="mt-2" style={{ maxWidth: '500px' }}>
        

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-2" controlId="formName">
            <Form.Label>Nimi</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-2" controlId="formEmail">
            <Form.Label>Sähköposti</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formTickets">
            <Form.Label>Lippujen määrä (max 10)</Form.Label>
            <Form.Control
              type="number"
              name="tickets"
              min={1}
              max={10}
              step={1}
              value={formData.tickets}
              onChange={handleChange}
              required
            />
          </Form.Group>

          {ticketTotal !== null && (
            <p className="ticket-total mb-3" aria-live="polite">
              Yhteensä: {ticketTotal} €
            </p>
          )}

          <Button
            variant="outline-dark"
            type="submit"
            className="reservation-button w-100 text-white"
            disabled={status === 'loading'}
          >
            {status === 'loading' ? 'Tarkistetaan...' : 'Tee varaus'}
          </Button>
        </Form>
      </Container>

      {/* 🪟 Popup Modal for messages */}
      <Modal show={showPopup} onHide={handleClosePopup} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {status === 'success' ? 'Kiitos varauksesta!' : 'Jotain meni pieleen'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: 'pre-line' }}>
          {status === 'success'
            ? 'Varaus vastaanotettu!\n\nLähetimme varausvahvistuksen sähköpostiisi.'
            : errorMessage + "\n\nOta tarvittaessa yhteyttä info@lcmynamaki.fi"}
        </Modal.Body>
        <Modal.Footer>
          <Button variant={status === 'success' ? 'success' : 'danger'} onClick={handleClosePopup}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="logot-bck"></div>
      <img className="logot" src={lionsImage} alt="Lions Clubin logo" />
      </div>
    </div>
  );
}

export default Reservation;
